import React, { useState } from 'react';
import { X, Shield, CreditCard, FileText, Phone } from 'lucide-react';

export const InsurancePopup = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('cashless');

  const insuranceCompanies = [
    'Care Health Insurance',
    'SBI General Insurance',
    'ICICI Lombard',
    'National Insurance',
    'The New India Assurance',
    'United India Insurance',
    'HDFC Ergo',
    'Bajaj Allianz General Insurance',
    'IFFCO Insurance Co.',
    'NVGT Care Pvt. Ltd',
    'Star Health Insurance',
    'Niva Bupa/Max Bupa',
    'Universal Sompo Health Care'
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b bg-white shrink-0">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
              Insurance Services
            </h2>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">
              Cashless & Reimbursement Facilities Available
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-4 flex-shrink-0 p-2 sm:p-3 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors border border-gray-300 min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Tab Navigation - Sticky */}
        <div className="border-b bg-white sticky top-0 z-10 shrink-0">
          <div className="flex">
            <button
              onClick={() => setActiveTab('cashless')}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 font-medium border-b-2 transition-colors ${
                activeTab === 'cashless'
                  ? 'border-blue-600 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <CreditCard className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-sm sm:text-base">Cashless Insurance</span>
            </button>
            <button
              onClick={() => setActiveTab('reimbursement')}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 font-medium border-b-2 transition-colors ${
                activeTab === 'reimbursement'
                  ? 'border-blue-600 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-sm sm:text-base">Reimbursement</span>
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 sm:p-6">
            {/* Tab Content */}
            {activeTab === 'cashless' && (
              <div className="space-y-6">
                <div className="bg-blue-50 rounded-lg p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-semibold text-blue-900 mb-3">
                    Cashless Insurance Services
                  </h3>
                  <p className="text-blue-800 mb-4 text-sm sm:text-base">
                    We provide hassle-free cashless insurance services where the insurance company 
                    directly settles your hospital bills. No need to pay from your pocket!
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-2 text-sm sm:text-base">Process:</h4>
                      <ul className="text-blue-800 text-xs sm:text-sm space-y-2">
                        <li>• Provide insurance card and ID proof</li>
                        <li>• Pre-authorization request submitted</li>
                        <li>• Treatment begins after approval</li>
                        <li>• Direct settlement with insurance company</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-2 text-sm sm:text-base">Documents Required:</h4>
                      <ul className="text-blue-800 text-xs sm:text-sm space-y-2">
                        <li>• Insurance Card</li>
                        <li>• Photo ID Proof</li>
                        <li>• Doctor's prescription</li>
                        <li>• Pre-authorization form</li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-4 p-3 sm:p-4 bg-white rounded-lg border">
                    <p className="text-xs sm:text-sm text-blue-700 font-semibold">
                      📝 Provide all documents at least 3 days prior to surgery for smooth processing.
                    </p>
                  </div>
                </div>

                {/* Insurance Companies */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-4 text-lg">Empanelled Insurance Companies (Cashless)</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                    {insuranceCompanies.map((company, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 rounded-lg p-2 sm:p-3 text-center border hover:shadow-md transition-shadow"
                      >
                        <Shield className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600 mx-auto mb-1 sm:mb-2" />
                        <span className="text-xs sm:text-sm font-medium text-gray-700 block break-words">
                          {company}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'reimbursement' && (
              <div className="space-y-6">
                <div className="bg-green-50 rounded-lg p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-semibold text-green-900 mb-3">
                    Reimbursement Process
                  </h3>
                  <p className="text-green-800 mb-4 text-sm sm:text-base">
                    In reimbursement process, you pay the hospital bills first and then get 
                    reimbursed from your insurance company later.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <h4 className="font-semibold text-green-900 mb-2 text-sm sm:text-base">Process:</h4>
                      <ul className="text-green-800 text-xs sm:text-sm space-y-2">
                        <li>• Pay hospital bills directly</li>
                        <li>• Collect all original documents</li>
                        <li>• Submit claim to insurance company</li>
                        <li>• Receive reimbursement amount</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-green-900 mb-2 text-sm sm:text-base">Required Documents:</h4>
                      <ul className="text-green-800 text-xs sm:text-sm space-y-2">
                        <li>• Original bills and receipts</li>
                        <li>• Discharge summary</li>
                        <li>• Investigation reports</li>
                        <li>• Claim form with doctor's signature</li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-4 p-3 sm:p-4 bg-white rounded-lg border">
                    <p className="text-xs sm:text-sm text-green-700 font-semibold">
                      ⚠️ Keep all original documents safe as they are required for reimbursement claims.
                    </p>
                  </div>
                </div>

                {/* Insurance Companies for Reimbursement */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-4 text-lg">Insurance Companies (Reimbursement)</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                    {insuranceCompanies.map((company, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 rounded-lg p-2 sm:p-3 text-center border hover:shadow-md transition-shadow"
                      >
                        <Shield className="w-4 h-4 sm:w-6 sm:h-6 text-green-600 mx-auto mb-1 sm:mb-2" />
                        <span className="text-xs sm:text-sm font-medium text-gray-700 block break-words">
                          {company}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-lg p-4 sm:p-6">
                  <h4 className="font-semibold text-yellow-900 mb-3 text-sm sm:text-base">Important Note</h4>
                  <p className="text-yellow-800 text-xs sm:text-sm">
                    In case there is delay in initial approval/final approval by insurance company 
                    for cashless service, patient should follow regular payment process and opt for 
                    reimbursement later.
                  </p>
                </div>
              </div>
            )}

            {/* Contact Information */}
            <div className="mt-6 sm:mt-8 p-4 sm:p-6 bg-gray-50 rounded-lg border">
              <h4 className="font-semibold text-gray-900 mb-3 text-sm sm:text-base">For Insurance Enquiries</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-xs sm:text-sm">
                <div className="flex items-center justify-center sm:justify-start space-x-2">
                  <Phone className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span>02067527445</span>
                </div>
                <div className="flex items-center justify-center sm:justify-start space-x-2">
                  <Phone className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span>7066901355</span>
                </div>
                <div className="flex items-center justify-center sm:justify-start space-x-2">
                  <Phone className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span>7559467108</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 sm:p-6 border-t bg-gray-50 shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-sm sm:text-base min-w-[80px] min-h-[44px] flex items-center justify-center"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};